/**
 * Run command - task execution through the core
 *
 * Dispatches a coding agent task as a supervised core run (`POST /api/core/exec`) and prints
 * the result when the run is done.
 */

import chalk from 'chalk';
import type { TaskOptions } from '@spacecode/shared/remote';
import { validateRepo, validateBranch, validatePrompt } from '../utils/validation.js';
import { generateTaskId } from '../utils/task.js';
import { setupRun, coreExec, warnIgnoredOptions } from '../utils/run-setup.js';
import { buildRemoteTask, statusFromResult, errorTaskStatus } from '../utils/remote-task.js';

/**
 * Run a task through the core and print the result when the run is done.
 *
 * Flow:
 * 1. Validate inputs
 * 2. Gather config files and env vars
 * 3. Call /api/core/exec (blocks until the run is done)
 * 4. Print logs and diff
 * 5. Save task record
 */
export async function run(options: TaskOptions): Promise<void> {
  const taskId = generateTaskId();
  warnIgnoredOptions(options);
  const validRepo = validateRepo(options.repo);
  const validPrompt = validatePrompt(options.prompt);
  const validBranch = options.branch ? validateBranch(options.branch) : undefined;

  // Set up SIGINT handler to abort the in-flight SSE request
  const abortController = new AbortController();
  const sigintHandler = () => {
    console.log(chalk.dim('\nReceived SIGINT, aborting stream...'));
    abortController.abort();
  };
  process.on('SIGINT', sigintHandler);

  console.log(chalk.bold('\nspacecode run\n'));
  console.log(chalk.dim(`  Task:   ${taskId}`));
  console.log(chalk.dim(`Repo:   ${validRepo}`));
  console.log(chalk.dim(`Prompt: ${validPrompt.substring(0, 60)}${validPrompt.length > 60 ? '...' : ''}`));
  if (validBranch) console.log(chalk.dim(`Branch: ${validBranch}`));
  console.log('');

  try {
    const { envVars } = await setupRun({ proxyMode: options.proxyMode });

    let exitCode = 0;
    let success = true;

    console.log(chalk.dim('---'.repeat(20)));

    // The core answers when the run is done, so there is no live stream.
    const viaCore = await coreExec({ repo: validRepo, prompt: validPrompt, envVars, branch: validBranch }, abortController.signal);
    if (viaCore.logs) console.log(viaCore.logs);
    if (viaCore.diff) {
      console.log(chalk.dim('\nDiff:'));
      console.log(viaCore.diff);
    }
    success = viaCore.success;
    exitCode = viaCore.exitCode;

    console.log(chalk.dim('---'.repeat(20)));

    // Save task record
    const { addTask } = await import('../config.js');
    const task = buildRemoteTask({
      id: taskId,
      repo: validRepo,
      prompt: validPrompt,
      mode: 'stream',
      status: statusFromResult(success),
      exitCode,
      branch: validBranch,
    });
    await addTask(task);

    if (success) {
      console.log(chalk.green('Task completed successfully.'));
    } else {
      console.log(chalk.red(`Task failed (exit code ${exitCode}).`));
      process.exit(exitCode || 1);
    }
  } catch (error) {
    const isAbort = error instanceof Error && error.name === 'AbortError';

    // Save task record
    try {
      const { addTask } = await import('../config.js');
      const task = buildRemoteTask({
        id: taskId,
        repo: validRepo,
        prompt: validPrompt,
        mode: 'stream',
        status: errorTaskStatus(isAbort),
        error: isAbort ? 'Cancelled by user' : (error instanceof Error ? error.message : String(error)),
      });
      await addTask(task);
    } catch (saveErr) {
      console.warn(`Failed to save task record: ${saveErr instanceof Error ? saveErr.message : String(saveErr)}`);
    }

    if (isAbort) {
      console.log(chalk.yellow('\nStream cancelled.'));
      process.exit(130);
    } else {
      console.error(chalk.red(`\nError: ${error instanceof Error ? error.message : String(error)}`));
      process.exit(1);
    }
  } finally {
    process.removeListener('SIGINT', sigintHandler);
  }
}
